'use client';

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserById } from '../../hooks/use-user';
import { useAuthStore, selectIsAuthenticated, selectIsLoading } from '../../hooks';

/**
 * ProtectedRoute
 *
 * Protects routes that require authentication.
 * Supports both:
 * - Passport SSO (omni_session cookie)
 * - Wallet-based auth (Thirdweb)
 *
 * If user is authenticated via Passport SSO, they don't need a wallet connected.
 */
export function ProtectedRoute({ children, requireVerified = false }) {
  const navigate = useNavigate();
  const { walletAddress } = useAuthStore();

  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isAuthLoading = useAuthStore(selectIsLoading);
  const storeUser = useAuthStore((state) => state.user);

  // Only fetch user by wallet if we have a wallet AND aren't already authenticated via Passport
  const shouldFetchByWallet = walletAddress && !storeUser?.passportId;

  const {
    data: walletUser,
    isLoading: isLoadingWalletUser,
    isError: isWalletUserError,
  } = useGetUserById(shouldFetchByWallet ? walletAddress : null);

  // Use Passport user from store, or fall back to wallet-fetched user
  const user = storeUser || walletUser;
  // Don't keep loading if we got an error (e.g., 404 user not found)
  const isLoadingUser = isAuthLoading || (shouldFetchByWallet && isLoadingWalletUser && !isWalletUserError);

  useEffect(() => {
    // Wait for auth check to complete
    if (isLoadingUser) {
      return;
    }

    // If user is authenticated (either via Passport or wallet), check requirements
    if (isAuthenticated && user) {
      // If verification is required but user not verified
      if (requireVerified && !user.isVerified) {
        navigate('/complete-registration');
        return;
      }
      // User is good to go
      return;
    }

    // Not authenticated - redirect to sign-in
    // (Sign-in page will redirect to Passport if needed)
    navigate('/sign-in');
  }, [
    walletAddress,
    user,
    isLoadingUser,
    isAuthenticated,
    requireVerified,
    navigate,
  ]);

  // Show loading while checking user status
  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // If not authenticated after loading, don't render children
  if (!isAuthenticated || !user) {
    return null;
  }

  return children;
}

/**
 * UnverifiedOnlyRoute
 *
 * For complete registration flow - only allows unverified users.
 * Redirects verified users to their profile.
 */
export function UnverifiedOnlyRoute({ children }) {
  const navigate = useNavigate();
  const { walletAddress } = useAuthStore();

  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isAuthLoading = useAuthStore(selectIsLoading);
  const storeUser = useAuthStore((state) => state.user);

  const shouldFetchByWallet = walletAddress && !storeUser?.passportId;
  const { data: walletUser, isLoading: isLoadingWalletUser, isError: isWalletUserError } = useGetUserById(
    shouldFetchByWallet ? walletAddress : null
  );

  const user = storeUser || walletUser;
  const isLoadingUser = isAuthLoading || (shouldFetchByWallet && isLoadingWalletUser && !isWalletUserError);

  useEffect(() => {
    if (isLoadingUser) return;

    // If no user at all, redirect to sign-in
    if (!user && !isAuthenticated) {
      navigate('/sign-in');
      return;
    }

    // If user is already verified, redirect to profile
    if (user && user.isVerified) {
      const profileId = user.passportId || user.oid || walletAddress;
      navigate('/profile/' + profileId);
      return;
    }
  }, [walletAddress, user, isLoadingUser, isAuthenticated, navigate]);

  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return children;
}

/**
 * PublicOnlyRoute
 *
 * For sign-in, sign-up pages that logged-in users shouldn't see.
 * Redirects authenticated & verified users to home.
 */
export function PublicOnlyRoute({ children }) {
  const navigate = useNavigate();
  const { walletAddress } = useAuthStore();

  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isAuthLoading = useAuthStore(selectIsLoading);
  const storeUser = useAuthStore((state) => state.user);

  const shouldFetchByWallet = walletAddress && !storeUser?.passportId;
  const { data: walletUser, isLoading: isLoadingWalletUser, isError: isWalletUserError } = useGetUserById(
    shouldFetchByWallet ? walletAddress : null
  );

  const user = storeUser || walletUser;
  const isLoadingUser = isAuthLoading || (shouldFetchByWallet && isLoadingWalletUser && !isWalletUserError);

  useEffect(() => {
    if (isLoadingUser) return;

    // If user is logged in and verified, redirect to home
    if (user && isAuthenticated && user.isVerified) {
      navigate('/');
      return;
    }

    // If user exists but not verified, redirect to complete registration
    if (user && isAuthenticated && !user.isVerified) {
      navigate('/complete-registration');
      return;
    }
  }, [user, isLoadingUser, isAuthenticated, navigate]);

  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return children;
}
